import { LockDesignerDoorType, LockDesignerSelections, LockDesignerProduct, LockDesignerFilters } from 'Api/apis';
import { OptionChanged } from 'Vue/interfaces/LockDesigner';
import { createStore } from 'vuex';
import { extend } from '@vue/shared';
import store from '.';

// We create blank component
const component = extend({});

type VueComponent = InstanceType<typeof component>;
export interface IModalProps {
    component: null | VueComponent;
    props?: object;
}

export const lockDesignerStore: any = createStore({
    state: {
        isLoading: false,
        isLoadingDefault: true,
        modalState: { component: null, props: {} },
        language: 0,
        backToRoute: null,
        authToken: null,
        compatibilityModal: null,
        isFinishLoading: false,
        selections: {
            function: null,
            doorPrep: null,
            doorStyle: null,
            insideHandle: null,
            insideEscutcheon: null,
            insidePrivacyBolt: null,
            insideBackplate: null,
            outsideHandle: null,
            outsideEscutcheon: null,
            outsidePrivacyBolt: null,
            doorFitFinderEnabled: false,
            scene7ImageUrl: null,
            exterior: true,
            syncedFinish: '',
        },
        doorFitFinder: {
            activeComponent: 'intro',
            prevQuestion: '',
            activeQuestion: 'Q1',
            existingHardware: '',
            lock: null,
            doorEdge: '',
        },
        filters: [],
        componentGroups: [],
        filterOptions: [],
    },
    mutations: {
        openModal(state, payload) {
            const { props, component } = payload;
            state.modalState = { component, props: props || {} };
        },
        closeModal(state) {
            state.modalState = { component: null, props: {} };
        },
        setBackToRoute(state, route) {
            state.backToRoute = route;
        },
        setCompatibilityModal(state, value) {
            state.compatibilityModal = value;
        },
        updateSelections(state, selections) {
            state.selections = { ...state.selections, ...selections };
        },
        updateComponentGroups(state, componentGroups) {
            state.componentGroups = [...componentGroups];
        },
        updatePreviousQuestion(state, value) {
            state.doorFitFinder.prevQuestion = value;
        },
        updateActiveComponent(state, value) {
            state.doorFitFinder.activeComponent = value;
        },
        updateActiveQuestion(state, value) {
            state.doorFitFinder.activeQuestion = value;
        },
        removeSetDFFStates(state, value) {
            state.doorFitFinder.lock = null;
            state.doorFitFinder.doorEdge = '';
        },
        resetDoorFitFinderSelections(state, value) {
            state.doorFitFinder = value;
        },
        setAnsweredQuestionData(state, value) {
            state.doorFitFinder = { ...state.doorFitFinder, ...value };
        },
        setIsLoading(state, isLoading) {
            state.isLoading = isLoading;
        },
        setIsLoadingDefault(state, isLoadingDefault) {
            state.isLoadingDefault = isLoadingDefault;
        },
        setIsFinishLoading(state, value) {
            state.isFinishLoading = value;
        },
        updateAuthToken(state, token) {
            state.authToken = token;
        },
        setFilters(state, filters) {
            state.filters = filters;
        },
        setComponentGroupFilters(state, componentGroups) {
            const incomingGroup = componentGroups.find((group) => group.components?.some((component) => component.vueComponent === 'ProductFilters'));
            let incomingComponentIndex = -1;
            let stateComponentGroups = [...state.componentGroups];
            if (incomingGroup) {
                incomingComponentIndex = incomingGroup.components.findIndex((component) => component.vueComponent === 'ProductFilters');

                stateComponentGroups.map((stateGroup) => {
                    const componentIndex = stateGroup.components?.findIndex((component) => component.vueComponent === 'ProductFilters');
                    if (componentIndex > -1) {
                        stateGroup.components[componentIndex].filters = incomingGroup.components[incomingComponentIndex].filters;
                        stateGroup.components[componentIndex].productCount = incomingGroup.components[incomingComponentIndex].productCount;
                    }
                    return stateGroup;
                });
            }
            state.componentGroups = stateComponentGroups;
        },
        setComponentGroupProducts(state, componentGroups) {
            const incomingGroup = componentGroups.find((group) => group.components?.some((component) => component.vueComponent === 'Products'));
            let incomingComponentIndex = -1;
            let stateComponentGroups = [...state.componentGroups];
            if (incomingGroup) {
                incomingComponentIndex = incomingGroup.components.findIndex((component) => component.vueComponent === 'Products');
                stateComponentGroups.map((stateGroup) => {
                    const componentIndex = stateGroup.components?.findIndex((component) => component.vueComponent === 'Products');
                    if (componentIndex > -1) {
                        stateGroup.components[componentIndex].products = incomingGroup.components[incomingComponentIndex].products;
                    }
                    return stateGroup;
                });
            }
            state.componentGroups = stateComponentGroups;
        },
    },
    actions: {
        openModal({ commit }, payload) {
            commit('openModal', payload);
        },
        closeModal({ commit }) {
            commit('closeModal');
        },
        setIsLoading({ commit }, isLoading) {
            commit('setIsLoading', isLoading);
        },
        setIsLoadingDefault({ commit }, isLoadingDefault) {
            commit('setIsLoadingDefault', isLoadingDefault);
        },

        setSelections({ commit }, selections) {
            commit('updateSelections', selections);
        },

        setComponentGroups({ commit }, componentGroups) {
            commit('updateComponentGroups', componentGroups);
        },

        async resetComponentGroups({ commit, dispatch }) {
            await dispatch('setComponentGroups', []);
            commit('setBackToRoute', null);
            commit('setCompatibilityModal', null);
        },

        async updateSelections({ commit, dispatch }, data) {
            commit('setIsLoading', true);
            try {
                return await dispatch('updateSelectionField', data).then(async (updatedSelection) => await dispatch('fetchSelections', { selections: updatedSelection, optionChanged: data.optionChanged }));
            } finally {
                setTimeout(() => {
                    commit('setIsLoading', false);
                }, 500); // set loading false after the asynchronous tasks are complete
            }
        },

        async updateDoorTypeSelections({ dispatch, commit }, doorTypeData) {
            commit('setIsLoading', true);
            await dispatch('updateSelectionField', { ...doorTypeData, updateFilter: true }).then(async (updatedSelection) => await dispatch('fetchDoorType', { selections: updatedSelection, doorTypeOption: doorTypeData.doorTypeOption }));
            setTimeout(() => {
                commit('setIsLoading', false);
            }, 500); // set loading false after the asynchronous tasks are complete
        },

        async fetchUpdatedProducts({ dispatch, state, commit }, productData) {
            if (!productData.filters && productData.reset !== true) {
                productData.filters = [...state.filters];
            }
            await dispatch('updateSelectionField', productData).then(async (updatedSelection) => await dispatch('fetchProduct', { selections: updatedSelection, ...productData }));
            commit('setIsFinishLoading', false);
        },

        async updateSingleFilter({ dispatch, state, commit }, filterData) {
            let filtersState = [...state.filters];
            try {
                filtersState = [...state.filters];
                let formattedFilters = await dispatch('formatFilters', filterData?.filterValues);
                let index = filtersState.findIndex((filter) => filter.name === filterData.slug);
                if (index >= 0) {
                    filtersState[index].values = [...formattedFilters];
                } else {
                    filtersState.push({ name: filterData.slug, values: formattedFilters });
                }

                commit('setFilters', filtersState);
            } catch (err) {
                // handle error here
            }

            return filtersState;
        },
        async updateMultipleFilters({ dispatch, state, commit }, filterData) {
            let filtersState = [...state.filters];
            let formattedFiltersArray = await dispatch('formatFilterArray', filterData?.filters);
            if (filterData.reset) {
                formattedFiltersArray = formattedFiltersArray.map((filters: any) => filters.name);
                filtersState = filtersState.filter((filters) => {
                    !formattedFiltersArray.includes(filters.name);
                });
            }

            commit('setFilters', filtersState);
            return filtersState;
        },
        // this is called from product page
        // it does not do a full refresh of componentGroups it only updates product array, filter array and product count
        // this was done to prevent a full refresh on the componentGroup
        async updateProductFilters({ dispatch, commit, state }, filterData) {
            commit('setIsLoading', true);
            let filtersState = [...state.filters];
            if (filterData.updateMany) {
                filtersState = await dispatch('updateMultipleFilters', filterData);
            } else {
                filtersState = await dispatch('updateSingleFilter', filterData);
            }

            const result = await dispatch('fetchUpdatedProducts', { ...filterData, filters: filtersState, setFiltersOnly: true });

            setTimeout(() => {
                commit('setIsLoading', false);
            }, 500); // set loading false after the asynchronous tasks are complete

            return result;
        },
        formatFilterArray({}, filterArray) {
            return filterArray
                .map((FilterCategory) => {
                    return { name: FilterCategory.slug, values: FilterCategory.filterValues.filter((filterItem) => filterItem.isSelected).map((filterItem) => filterItem.title) };
                })
                .filter((FilterCategory) => FilterCategory.values.length > 0);
        },
        formatFilters({}, filterValues) {
            return filterValues.filter((filterItem) => filterItem.isSelected).map((filterItem) => filterItem.title);
        },

        async removeLock({state, commit, dispatch}, data) {
            const selectionObject = { ...state.selections };
            if(data.clearSelections?.length) {
                data.clearSelections.forEach((item) => {
                    let theProp = null;
                    if(item.includes('.')) {
                       theProp = item.split('.')[1];
                   } else {
                       theProp = item;
                   }
                    selectionObject[theProp] = null;
                });
            }

            let modifiedSelectionObject = { ...selectionObject };
            await dispatch('fetchSelections', { selections: modifiedSelectionObject, optionChanged: "None" })
        },
        // used to set the new value of the selection field
        async updateSelectionField({ state, dispatch, commit }, data) {
            // we might want to remove this logic and make an update to the api we know its going to update selection state
            let regex = /\bselections\.([^.]+)(?:\.([^.]+))?\b/;
            let matches = regex.exec(data.fieldId);

            if (matches) {
                let field = matches[1]; //
                const filters = [...state.filters];

                const selectionObject = { ...state.selections };
                if (field) {
                    if (data.updateFilter) {
                        const filterIndex = filters.findIndex((filter) => filter.name === field);
                        if (filterIndex > -1) {
                            filters[filterIndex].values = [];
                            filters[filterIndex].values = [data?.selection?.displayName];
                            this.commit('setFilters', filters);
                        }
                    }

                    if (data.syncedFinish) {
                        selectionObject.syncedFinish = data.syncedFinish;
                    }

                    selectionObject[field] = data.selection;
                }

                return selectionObject;
            }
        },
        // select the default selections for the landing page
        async fetchDefaultSelections({ commit, dispatch, state }) {
            commit('setIsLoading', true);
            const selection = await dispatch('fetchSelection', { optionChanged: 'Defaults', selections: state.selections, exterior: state.selections.exterior }).then((selection) => {
                return selection;
            });

            setTimeout(() => {
                commit('setIsLoading', false);
            }, 500); // set loading false after the asynchronous tasks are complete
            return selection.selections;
        },

        async fetchAdvancedFilters({ state, commit, dispatch }) {
            let filtersReq = state.filters;
            const response = await LockDesignerFilters({ filters: filtersReq, selections: state.selections });
            await dispatch('formatFilterArray', response.filters).then((filters) => commit('setFilters', filters));
            return response;
        },

        async fetchSelection({ state, commit }, data) {
            const selection = await LockDesignerSelections({ ...data });
            commit('updateSelections', selection.selections);
            commit('setIsLoadingDefault', false);
            return selection;
        },

        async fetchSelections({ commit, state, dispatch }, data) {
            !data?.excludeLoader && commit('setIsLoading', true);
            let useSelections = !!data?.selections ? data.selections : state.selections;
            await dispatch('fetchSelection', { selections: useSelections, exterior: useSelections.exterior, optionChanged: data.optionChanged }).then((selection) => {
                commit('updateComponentGroups', selection.componentGroups);
                commit('setBackToRoute', selection.backToRoute);
                commit('setCompatibilityModal', selection.compatibilityModal);
                if (!data?.excludeLoader) {
                    setTimeout(() => {
                        commit('setIsLoading', false);
                    }, 500); // set loading false after the asynchronous tasks are complete
                }
                return selection;
            });
        },
        async updateAdvancedFilters({ state, commit, dispatch }, filtersData) {
            commit('setIsLoading', true);
            return await dispatch('updateSingleFilter', { ...filtersData, ignoreFetchProducts: true })
                .then(async () => {
                    return await dispatch('fetchAdvancedFilters');
                })
                .then(async (response) => {
                    await dispatch('fetchSelections', { selections: response.selections, optionChanged: 'None', excludeLoader: true });
                    setTimeout(() => {
                        commit('setIsLoading', false);
                    }, 500); // set loading false after the asynchronous tasks are complete
                    return response;
                });
        },
        async fetchProduct({ commit, state }, data) {
            commit('setIsLoading', true);
            const product = await LockDesignerProduct({
                selections: !!data?.selections ? data.selections : state.selections,
                productType: data.productType,
                filters: data.filters,
                filterOptions: { pageNumber: 1, resultsPerPage: 100 },
                searchTerm: data.searchTerm,
            });
            commit('updateSelections', product.selections);
            commit('setBackToRoute', product.backToRoute);
            commit('setIsLoadingDefault', false);
            if (data.setFiltersOnly) {
                commit('setComponentGroupFilters', product.componentGroups);
                commit('setComponentGroupProducts', product.componentGroups);
                return;
            }
            commit('updateComponentGroups', product.componentGroups);

            setTimeout(() => {
                commit('setIsLoading', false);
            }, 500);
        },

        async fetchDoorType({ commit, state }, data) {
            commit('setIsLoading', true);
            const selection = await LockDesignerDoorType({ selections: !!data?.selections ? data.selections : state.selections, doorTypeOption: data.doorTypeOption });
            commit('updateSelections', selection.selections);
            commit('updateComponentGroups', selection.componentGroups);
            commit('setBackToRoute', selection.backToRoute);
            commit('setCompatibilityModal', selection.compatibilityModal);
            setTimeout(() => {
                commit('setIsLoading', false);
            }, 500);
        },
    },
    getters: {
        compatibilityModalProps: (state) => {
            return state.compatibilityModal;
        },
        componentGroups: (state) => {
            return state.componentGroups;
        },
        selections: (state) => {
            return state.selections;
        },
        imageUrl: (state) => {
            return state.selections.scene7ImageUrl;
        },
        backToRoute: (state) => {
            return state.backToRoute;
        },
        modalState: (state) => {
            return state.modalState;
        },
        isLoadingDefault: (state) => {
            return state.isLoadingDefault;
        },
        isLoading: (state) => {
            return state.isLoading;
        },
        filters: (state) => {
            return state.filters;
        },
    },
    modules: {},
});
